import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import UserAvatar from '../common/UserAvatar';
import useSupportUnread from '../../hooks/useSupportUnread';
import { 
  LayoutDashboard, Users, Calendar, Settings, LogOut, FileText, 
  Tag, AlertTriangle, BarChart3, UserCog, Grid3X3, Shield, Wallet,
  Menu, X, PieChart, Database, CreditCard, Flag, MapPin, Image, ShieldCheck, ChevronDown, Package, MessageSquare, Megaphone
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Grouped menu configuration (same as desktop)
const menuGroups = [
  {
    label: 'Overview',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
      { path: '/admin/reports', icon: BarChart3, label: 'Reports', module: 'reports' },
      { path: '/admin/category-analytics', icon: PieChart, label: 'Category Analytics', module: 'reports' },
    ]
  },
  {
    label: 'Users & Providers',
    items: [
      { path: '/admin/users', icon: UserCog, label: 'Users', module: 'customers', adminOnly: true },
      { path: '/admin/providers', icon: Users, label: 'Providers', module: 'providers' },
      { path: '/admin/messages', icon: MessageSquare, label: 'Messages', module: 'support_chat' },
    ]
  },
  {
    label: 'Operations',
    items: [
      { path: '/admin/bookings', icon: Calendar, label: 'Bookings', module: 'bookings' },
      { path: '/admin/categories', icon: Grid3X3, label: 'Categories', module: 'categories' },
      { path: '/admin/services', icon: Package, label: 'Services', module: 'services' },
      { path: '/admin/insurance-claims', icon: ShieldCheck, label: 'Insurance Claims', module: 'bookings' },
    ]
  },
  {
    label: 'Finance',
    items: [
      { path: '/admin/transactions', icon: CreditCard, label: 'Transactions', adminOnly: true },
      { path: '/admin/withdrawals', icon: Wallet, label: 'Withdrawals', module: 'withdrawals' },
    ]
  },
  {
    label: 'Moderation',
    items: [
      { path: '/admin/disputes', icon: AlertTriangle, label: 'Disputes', module: 'disputes' },
      { path: '/admin/user-reports', icon: Flag, label: 'User Reports', module: 'disputes', adminOnly: true },
    ]
  },
  {
    label: 'Marketing',
    items: [
      { path: '/admin/banners', icon: Image, label: 'Banners', module: 'cms' },
      { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes', module: 'promo_codes' },
      { path: '/admin/cms', icon: FileText, label: 'CMS', module: 'cms' },
      { path: '/admin/outreach', icon: Megaphone, label: 'Outreach', adminOnly: true },
    ]
  },
  {
    label: 'System',
    items: [
      { path: '/admin/locations', icon: MapPin, label: 'Locations', adminOnly: true },
      { path: '/admin/audit-logs', icon: Shield, label: 'Audit Logs', adminOnly: true },
      { path: '/admin/data-management', icon: Database, label: 'Data Management', adminOnly: true },
      { path: '/admin/settings', icon: Settings, label: 'Settings', adminOnly: true },
    ]
  },
];

export default function AdminMobileNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unread: supportUnread } = useSupportUnread();
  const [isOpen, setIsOpen] = useState(false);
  const [siteLogo, setSiteLogo] = useState(null);
  const [siteTitle, setSiteTitle] = useState('HandyBayani');
  const [siteUrl, setSiteUrl] = useState('https://handybayani.com');
  const [profileImage, setProfileImage] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    // Fetch site identity (logo)
    axios.get(`${API}/cms/landing`)
      .then(res => {
        const siteIdentity = res.data?.site_identity;
        if (siteIdentity?.site_logo) {
          const logoUrl = siteIdentity.site_logo.startsWith('http') 
            ? siteIdentity.site_logo 
            : `${BACKEND_URL}${siteIdentity.site_logo}`;
          setSiteLogo(logoUrl);
          setLogoError(false);
        }
        if (siteIdentity?.site_title) {
          setSiteTitle(siteIdentity.site_title);
        }
        if (siteIdentity?.site_url) {
          setSiteUrl(siteIdentity.site_url);
        }
      })
      .catch(() => {});
    
    // Fetch profile image
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data?.profile_image) {
            const imgUrl = res.data.profile_image.startsWith('http') 
              ? res.data.profile_image 
              : `${BACKEND_URL}${res.data.profile_image}`;
            setProfileImage(imgUrl);
          }
        })
        .catch(() => {});
    }
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  const toggleGroup = (groupLabel) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  };

  const getVisibleGroups = () => {
    return menuGroups.map(group => {
      const visibleItems = group.items.filter(item => {
        if (user?.role === 'admin') return true;
        if (['manager', 'customer_service', 'accountant'].includes(user?.role)) {
          if (item.adminOnly) return false;
          const permissions = user?.permissions || {};
          if (item.module && permissions[item.module]?.can_view) return true;
          if (item.path === '/admin' && Object.values(permissions).some(p => p.can_view)) return true;
        }
        return false;
      });
      return { ...group, items: visibleItems };
    }).filter(group => group.items.length > 0);
  };

  const visibleGroups = getVisibleGroups();

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Header - only visible on mobile */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-slate-900 z-50 h-14 flex items-center justify-between px-4">
        <a 
          href={siteUrl}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          target="_blank"
          rel="noopener noreferrer"
        >
          {siteLogo && !logoError ? (
            <img 
              src={siteLogo} 
              alt={siteTitle}
              className="w-8 h-8 rounded-lg object-cover"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
              <img src="/handybayani-logo.png" alt="HandyBayani" className="w-6 h-6 object-contain" />
            </div>
          )}
          <span className="text-white font-bold font-[Poppins]">{siteTitle}</span>
        </a>
        
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative w-10 h-10 flex items-center justify-center text-white"
          data-testid="admin-mobile-menu-btn"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          {!isOpen && supportUnread > 0 && (
            <span
              className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold"
              data-testid="mobile-support-unread-dot"
            >
              {supportUnread > 9 ? '9+' : supportUnread}
            </span>
          )}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Slide-out Menu */}
      <nav 
        className={`md:hidden fixed top-14 right-0 bottom-0 w-72 bg-slate-900 z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="admin-mobile-menu"
      >
        {/* Role badge */}
        {user?.role === 'manager' && (
          <div className="m-4 px-4 py-2 bg-indigo-500/20 rounded-xl border border-indigo-500/30">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-indigo-400" />
              <span className="text-sm text-indigo-300 font-medium">Manager</span>
            </div>
          </div>
        )}

        <div className="p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
          {visibleGroups.map((group, groupIndex) => (
            <div key={group.label} className={groupIndex > 0 ? 'mt-3' : ''}>
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.label)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                <span>{group.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${collapsedGroups[group.label] ? '-rotate-90' : ''}`} />
              </button>
              
              {/* Group Items */}
              {!collapsedGroups[group.label] && (
                <div className="mt-1 space-y-0.5">
                  {group.items.map((item) => (
                    <button
                      key={item.path}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                        isActive(item.path)
                          ? 'bg-slate-800 text-white'
                          : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
                      }`}
                      onClick={() => handleNavClick(item.path)}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.module === 'support_chat' && supportUnread > 0 && (
                        <span className="min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                          {supportUnread > 99 ? '99+' : supportUnread}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* User info and logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <UserAvatar 
              src={profileImage} 
              name={user?.name} 
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-300 font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button 
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </nav>
    </>
  );
}
